import type { ManifestParser } from './types';
import { parseCargoToml } from './cargo-toml';
import { parseGemfileLock } from './gemfile-lock';
import { parseGoMod } from './go-mod';
import { parseNuget } from './nuget';
import { parsePackageJson } from './package-json';
import { parsePomXml } from './pom-xml';
import { parseRequirementsTxt } from './requirements-txt';

/** The manifest formats PatchProbe can audit. */
export const MANIFEST_KINDS = [
  'package.json',
  'requirements.txt',
  'go.mod',
  'Cargo.toml',
  'pom.xml',
  'nuget',
  'Gemfile.lock',
] as const;
export type ManifestKind = (typeof MANIFEST_KINDS)[number];

/** Human-facing labels for a manifest-type selector. */
export const MANIFEST_LABELS: Record<ManifestKind, string> = {
  'package.json': 'package.json (npm)',
  'requirements.txt': 'requirements.txt (PyPI)',
  'go.mod': 'go.mod (Go)',
  'Cargo.toml': 'Cargo.toml (Cargo)',
  'pom.xml': 'pom.xml (Maven)',
  nuget: '.csproj / packages.config (NuGet)',
  'Gemfile.lock': 'Gemfile.lock (RubyGems)',
};

export const MANIFEST_PARSERS: Record<ManifestKind, ManifestParser> = {
  'package.json': parsePackageJson,
  'requirements.txt': parseRequirementsTxt,
  'go.mod': parseGoMod,
  'Cargo.toml': parseCargoToml,
  'pom.xml': parsePomXml,
  nuget: parseNuget,
  'Gemfile.lock': parseGemfileLock,
};

/**
 * Guesses the manifest kind from a filename (preferred) or the content itself.
 * Returns null when neither gives a confident answer — the caller should ask
 * the user to pick a format explicitly.
 */
export function detectManifestKind(content: string, filename?: string): ManifestKind | null {
  if (filename) {
    const lower = filename.toLowerCase();
    if (lower.endsWith('package.json')) return 'package.json';
    if (lower.endsWith('requirements.txt') || lower.endsWith('requirements.in')) {
      return 'requirements.txt';
    }
    if (lower.endsWith('go.mod')) return 'go.mod';
    if (lower.endsWith('cargo.toml')) return 'Cargo.toml';
    if (lower.endsWith('pom.xml')) return 'pom.xml';
    if (
      lower.endsWith('.csproj') ||
      lower.endsWith('packages.config') ||
      lower.endsWith('.props')
    ) {
      return 'nuget';
    }
    if (lower.endsWith('gemfile.lock')) return 'Gemfile.lock';
  }

  const text = content.trim();
  // JSON with a dependencies-ish key → package.json.
  if (text.startsWith('{')) {
    try {
      const obj = JSON.parse(text) as Record<string, unknown>;
      if ('dependencies' in obj || 'devDependencies' in obj || 'name' in obj) {
        return 'package.json';
      }
    } catch {
      return null;
    }
  }
  if (/<PackageReference\b|<package\s+id=/.test(text)) return 'nuget';
  if (/<project\b[^>]*>|<dependency>/i.test(text) && /<artifactId>/i.test(text)) return 'pom.xml';
  if (/^module\s+\S+/m.test(text) && /^(require|go)\s/m.test(text)) return 'go.mod';
  if (
    /^\[(dependencies|dev-dependencies|build-dependencies)/m.test(text) ||
    /^\[package\]/m.test(text)
  ) {
    return 'Cargo.toml';
  }
  if (/^GEM$/m.test(text) && /^\s+specs:$/m.test(text)) return 'Gemfile.lock';
  if (/^[A-Za-z0-9_.-]+(\[[^\]]*\])?==\S+/m.test(text)) return 'requirements.txt';
  return null;
}
